import { Component, HostListener, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PanService } from "../../core/services/pan.service";
import { ReportOverviewState } from "../pan-verify-result/pan-verify-result.component";
import { UserService } from "../../core/services/user-service.service";
import { CommonService } from "src/app/core/services/common.service";
import { TableColumn } from "./raw-data-table/raw-data-table.component";
import { DatePipe } from "@angular/common";
import { GSTFilingRecord } from "./gst-compliance-card/gst-compliance-card.component";
import { getGSTFilingStatus } from "./utils/helpers";
import {
  downloadDashboard,
  downloadDashboardAsPDF,
} from "src/app/core/helpers/helper";

@Component({
  selector: "app-ngo-diligence",
  templateUrl: "./ngo-diligence.component.html",
  styleUrls: ["./ngo-diligence.component.scss"],
  providers: [DatePipe],
})
export class NgoDiligenceComponent implements OnInit {
  reportOverviewState: ReportOverviewState | null = null;
  ngoData: any = {};
  loading: boolean = false;
  error: string = "";
  txnId: string = "";
  processingTime: string = "";
  isReloading: boolean = false;

  createdDate: string = "";

  incomeTaxColumns: TableColumn[] = [
    { key: "registrationType", label: "Registration Type" },
    { key: "section", label: "Section" },
    {
      key: "registrationDate",
      label: "Registration Date",
      format: (value: any) => {
        if (!value) return "N/A";
        try {
          const date = new Date(value).toISOString();
          return this.formatDate(date);
        } catch {
          return value || "N/A";
        }
      },
    },
    { key: "urn", label: "URN" },
    { key: "surrenderDate", label: "Surrender Date" },
    { key: "assessmentYear", label: "Assessment Year" },
    { key: "address", label: "Address" },
  ];

  status206ABColumns: TableColumn[] = [
    { key: "field", label: "Field" },
    { key: "value", label: "Value" },
  ];

  darpanColumns: TableColumn[] = [
    { key: "ngoName", label: "NGO Name" },
    { key: "darpanId", label: "DARPAN ID" },
    { key: "ngoType", label: "NGO Type" },
    { key: "registrationNo", label: "Registration Number" },
    { key: "registrationDate", label: "Registration Date" },
    { key: "address", label: "Address" },
    {
      key: "website",
      label: "Website",
      format: (value: any) => {
        if (!value || value === "N/A") return "N/A";
        return `<a href="${value}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">Visit</a>`;
      },
      renderHtml: true,
    },
    {
      key: "matchLevel",
      label: "Match Level",
      format: (value: any) => value || "N/A",
    },
  ];

  blacklistColumns: TableColumn[] = [
    { key: "name", label: "NGO Name" },
    { key: "nameCeoAndMd", label: "CEO/MD" },
    { key: "registrationNoAndDate", label: "Registration Details" },
    { key: "debarringDate", label: "Debarring Date" },
    {
      key: "matchConfidence",
      label: "Match Confidence",
      format: (value: any) =>
        (value || "").split("_").join(" ").toUpperCase() || "N/A",
    },
    { key: "address", label: "Address" },
    { key: "reference", label: "Reference" },
  ];

  fcraColumns: TableColumn[] = [
    { key: "associationName", label: "Association Name" },
    { key: "fcraRegistrationNumber", label: "FCRA Registration Number" },
    { key: "associationAddress", label: "Association Address" },
    { key: "pincode", label: "Pin Code" },
    { key: "sourceState", label: "Source State" },
    { key: "validityFromDate", label: "Validity From Date" },
    { key: "validityToDate", label: "Validity To Date" },
    { key: "lastRenewedDate", label: "Last Renewed Date" },
    {
      key: "matchLevel",
      label: "Match Level",
      format: (value: any) => value || "N/A",
    },
  ];

  gstFilingColumns: TableColumn[] = [
    { key: "rtntype", label: "Return Type" },
    { key: "retPrd", label: "Return Period" },
    { key: "dof", label: "Filing Date" },
    { key: "arn", label: "ARN" },
    { key: "status", label: "Status" },
    {
      key: "mof",
      label: "MOF",
    },
  ];

  companyBasicDetailsColumns: TableColumn[] = [
    { key: "field", label: "Field" },
    { key: "value", label: "Value" },
  ];

  directorSummaryColumns: TableColumn[] = [
    { key: "director_name", label: "Director Name" },
    { key: "din_number", label: "DIN Number" },
    { key: "start_date", label: "Start Date" },
  ];

  directorDetailsColumns: TableColumn[] = [
    { key: "field", label: "Field" },
    { key: "value", label: "Value" },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private panService: PanService,
    private userService: UserService,
    private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    // Get state data from history state (most reliable method)
    const historyState = history.state;

    this.txnId = this.route.snapshot.params["txnId"];

    if (historyState?.reportOverviewState) {
      this.reportOverviewState = historyState.reportOverviewState;
      this.callNgoSearch();
    }

    if (this.txnId) {
      this.getPreviousReport();
    }
  }

  callNgoSearch(): void {
    if (!this.reportOverviewState) {
      this.error = "Invalid state data";
      return;
    }

    this.loading = true;
    this.error = "";

    this.panService.ngoSearch(this.reportOverviewState).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response?.success) {
          this.ngoData = response?.data?.data;
          this.processingTime = this.getProcessingTime(
            this.ngoData?.processingTimeMs ?? 0,
          );
          this.userService.updateUserCredits(
            response?.data?.remainingCredits ?? 0,
          );
          this.createdDate = this.formatDate(new Date().toISOString());
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.error =
          error?.error?.message || "Failed to fetch NGO diligence data";
      },
    });
  }
  getProcessingTime(ms: number): string {
    const timeString = (ms / 1000).toFixed(4) + " seconds";
    return timeString;
  }
  getPreviousReport(): void {
    this.loading = true;
    this.error = "";

    this.panService.getPreviousReport(this.txnId).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response?.success) {
          const responseData = response?.data ?? {};
          this.ngoData = responseData?.searchResult ?? {};
          this.createdDate = this.formatDate(responseData?.createdAt ?? "");
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.error = error?.error?.message || "Failed to fetch previous report";
      },
    });
  }

  onChangePAN(): void {
    this.router.navigate(["/"]);
  }

  formatDate(date: string): string {
    if (!date) return "N/A";
    try {
      let dateObj: Date = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return "N/A";
      }
      return this.commonService.dateFormatter(dateObj.toISOString());
    } catch {
      return "N/A";
    }
  }

  get fcraMatches(): any[] {
    return this.ngoData?.databaseResults?.fcraMatches || [];
  }

  get fcraSyncDate(): string | undefined {
    const syncLog = this.ngoData?.syncLog;
    if (syncLog?.records) {
      const fcraSync = syncLog.records.find(
        (record: any) => record.target === "fcra",
      );
      return fcraSync?.lastSyncedAt;
    }
    return undefined;
  }

  get darpanMatches(): any[] {
    return this.ngoData?.databaseResults?.darpanMatches || [];
  }

  get darpanSyncDate(): string | undefined {
    const syncLog = this.ngoData?.syncLog;
    if (syncLog?.records) {
      const darpanSync = syncLog.records.find(
        (record: any) => record.target === "darpan",
      );
      return darpanSync?.lastSyncedAt;
    }
    return undefined;
  }

  get incomeTaxData(): any[] {
    return this.ngoData?.databaseResults?.incometaxData || [];
  }

  get incomeTaxSyncDate(): string | undefined {
    const syncLog = this.ngoData?.syncLog;
    if (syncLog?.records) {
      const incomeTaxSync = syncLog.records.find(
        (record: any) => record.target === "income_tax",
      );
      return incomeTaxSync?.lastSyncedAt;
    }
    return undefined;
  }

  get activeGSTData(): any[] {
    // Extract active GST records from surepassResults or other sources
    const gstinData = this.ngoData?.surepassResults?.gstinByPanData;

    if (gstinData?.success && gstinData?.data) {
      const gstinList = gstinData.data?.data?.gstin_list ?? [];
      return Array.isArray(gstinList) ? gstinList : [gstinList];
    }

    return [];
  }

  get gstFilingData(): any[] {
    const gstResults = this.ngoData?.surepassResults?.sandboxGstResults;
    const filings: GSTFilingRecord[] = [];

    Object.values(gstResults).forEach((gstinData: any) => {
      if (
        gstinData["success"] &&
        gstinData["complianceData"] &&
        typeof gstinData["complianceData"] === "object" &&
        "efiledList" in gstinData["complianceData"]
      ) {
        const complianceData = gstinData["complianceData"] as {
          efiledList: any[];
        };

        complianceData.efiledList.forEach((filing: any) => {
          filings.push({
            gstin: (gstinData["gstin"] as string) || "N/A",
            rtntype: (filing["rtntype"] as string) || "N/A",
            retPrd: (filing["ret_prd"] as string) || "N/A",
            dof: (filing["dof"] as string) || "N/A",
            arn: (filing["arn"] as string) || "N/A",
            status: (filing["status"] as string) || "N/A",
            mof: (filing["mof"] as string) || "N/A",
            valid: (filing["valid"] as string) || "N/A",
          });
        });
      }
    });

    // Sort by date (most recent first)
    return filings.sort((a, b) => b.dof.localeCompare(a.dof));
  }

  get gstIDS(): { activeGSTIDS: string[]; inactiveGSTIDS: string[] } {
    const activeGSTINS: string[] = [];
    const inactiveGSTINS: string[] = [];

    const activeData = this.activeGSTData;
    activeData.forEach((record: any) => {
      if (record?.gstin) {
        if (
          record?.active_status === "Active" ||
          record?.active_status === "active"
        ) {
          activeGSTINS.push(record.gstin);
        } else {
          inactiveGSTINS.push(record.gstin);
        }
      }
    });

    return {
      activeGSTIDS: activeGSTINS,
      inactiveGSTIDS: inactiveGSTINS,
    };
  }

  get status206ABData(): any {
    // Extract 206AB data from surepassResults or other sources
    const sandboxTds = this.ngoData?.surepassResults?.sandboxTdsResults;

    if (sandboxTds) {
      const tdsKey = Object.keys(sandboxTds)[0];
      if (tdsKey && sandboxTds[tdsKey]?.success) {
        return sandboxTds[tdsKey];
      }
    }
    return null;
  }

  get blacklistMatches(): any[] {
    return this.ngoData?.databaseResults?.blacklistedNgoMatches || [];
  }

  get blacklistSyncDate(): string | undefined {
    const syncLog = this.ngoData?.syncLog;
    if (syncLog?.records) {
      // Look for blacklist-related sync records
      const blacklistSync = syncLog.records.find(
        (record: any) =>
          record.target === "blacklist" || record.target === "ngo_org",
      );
      return blacklistSync?.lastSyncedAt;
    }
    return undefined;
  }

  get mcaData(): Record<string, any> {
    return this.ngoData?.mcaData || {};
  }

  get mcaDocPullEnabled(): boolean {
    return this.ngoData?.comprehensiveSummary?.mcaDocPullEnabled === true;
  }

  get legalCheckEnabled(): boolean {
    return this.ngoData?.comprehensiveSummary?.legalCheckEnabled === true;
  }

  get directorSummaryData(): any[] {
    const mcaData = this.mcaData;
    if (!mcaData["isCorporatePan"]) return [];
    return mcaData?.["companyDetails"]?.details?.directors || [];
  }

  get directorData(): any[] {
    const mcaData = this.mcaData;
    if (!mcaData["isCorporatePan"]) return [];
    return mcaData?.["directorDetails"] || [];
  }

  get incomeTaxExemptionTableData(): any[] {
    const incometaxData = this.ngoData?.databaseResults?.incometaxData || [];
    return incometaxData.map((item: any) => ({
      registrationType: item.sectionNumber?.includes("12A")
        ? "12A Registration"
        : item.sectionNumber?.includes("80G")
          ? "80G Registration"
          : "N/A",
      section: item.sectionNumber || "N/A",
      registrationDate: this.formatDate(item.dateOfRegistration) || "N/A",
      urn: item.uniqueRegistrationNumber || "N/A",
      surrenderDate: this.formatDate(item.dateOfSurrender) || "N/A",
      assessmentYear: item.ayDateFromRegistered || "N/A",
      address: this.formatAddress(item),
    }));
  }

  formatAddress(item: any): string {
    const parts = [
      item.flatDoorBuilding,
      item.roadStreetPostOffice,
      item.areaLocality,
      item.townCityDistrict,
      item.state,
      item.pincode,
    ].filter(Boolean);
    return parts.join(", ") || "N/A";
  }

  get status206ABTableData(): { [key: string]: any } {
    const statusData = this.status206ABData ?? {};
    const complianceData = statusData?.complianceData ?? {};

    return {
      PAN: statusData.pan || this.reportOverviewState?.pan || "N/A",
      "Name (Masked)": complianceData?.name || "N/A",
      "Financial Year": complianceData?.financialYear || "N/A",
      "PAN Status": complianceData?.panStatus || "N/A",
      "Specified Person (206AB/206CCA)":
        (complianceData?.specifiedPersonUs206ab206cca ??
          complianceData?.specified_person_us_206ab_206cca) === "n"
          ? "No"
          : (complianceData?.specifiedPersonUs206ab206cca ??
                complianceData?.specified_person_us_206ab_206cca) === "y"
            ? "Yes"
            : "N/A",
      "Transaction ID":
        statusData.transactionId || statusData?.transaction_id || "N/A",
    };
  }

  get incomeTaxRawData(): any[] {
    return this.ngoData?.databaseResults?.incometaxData || [];
  }

  get groupedGSTFilingData(): {
    gstin: string;
    state: string;
    filings: any[];
  }[] {
    const filings = this.gstFilingData;
    const activeGST = this.activeGSTData;
    const gstinSet = new Set(filings.map((f) => f.gstin));

    return Array.from(gstinSet).map((gstin) => {
      const gstinFilings = filings.filter((f) => f.gstin === gstin);
      const gstRecord = activeGST.find((g: any) => g.gstin === gstin);
      return {
        gstin: gstin,
        state: gstRecord?.state || "Unknown State",
        filings: gstinFilings,
      };
    });
  }

  get companyBasicDetailsTableData(): { [key: string]: any } {
    const mcaData = this.mcaData;
    if (!mcaData?.["isCorporatePan"]) return {};
    const companyDetailsObj = mcaData?.["companyDetails"] ?? {};
    const companyInfo = companyDetailsObj?.details?.company_info ?? ({} as any);
    return {
      "Company Name":
        companyDetailsObj?.companyName ||
        companyDetailsObj?.company_name ||
        "N/A",
      CIN: companyDetailsObj?.company_id || "N/A",
      "ROC Code": companyInfo?.roc_code || "N/A",
      "Registration Number": companyInfo?.registration_number || "N/A",
      "Company Category": companyInfo?.company_category || "N/A",
      "Class of Company": companyInfo?.class_of_company || "N/A",
      "Company Subcategory": companyInfo?.company_subcategory || "N/A",
      "Authorised Capital": companyInfo?.authorised_capital || "N/A",
      "Paid Up Capital": companyInfo?.paid_up_capital || "N/A",
      "Date of Incorporation": this.formatDate(
        companyInfo?.date_of_incorporation,
      ),
      "Company Status": companyInfo?.company_status || "N/A",
      "Listed Status": companyInfo?.listed_status || "N/A",
      "Email ID": companyInfo?.email_id || "N/A",
      "Last AGM Date": this.formatDate(companyInfo?.last_agm_date),
      "Registered Address": companyInfo?.registered_address || "N/A",
    };
  }

  get directorSummaryTableData(): any[] {
    const directorSummaryData = this.directorSummaryData;
    if (!directorSummaryData?.length) return [];

    return directorSummaryData.map((record: any) => ({
      director_name: record.director_name || "N/A",
      din_number: record.din_number || "N/A",
      start_date: this.formatDate(record.start_date) || "N/A",
    }));
  }

  get directorDetailsTableData(): { [key: string]: any }[] {
    const directors = this.directorData;
    return directors.map((director: any) => {
      const dinApiResponse = director?.dinApiResult ?? {};
      return {
        "Full Name": dinApiResponse.full_name || "N/A",
        "DIN Number": dinApiResponse.din_number || "N/A",
        "Father's Name": dinApiResponse.father_name || "N/A",
        "Date of Birth": this.formatDate(dinApiResponse.dob),
        Nationality: dinApiResponse.nationality || "N/A",
        "DIN Status": dinApiResponse.din_status || "N/A",
      };
    });
  }

  formatMatchConfidence(confidence: string): string {
    if (!confidence) return "High";
    if (confidence === "full") return "High";
    if (confidence === "partial") return "Medium";
    if (confidence === "no") return "Low";
    return confidence
      .split("_")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  getMatchConfidenceClass(confidence: string): string {
    if (confidence === "full") return "bg-green-100 text-green-800";
    if (confidence === "partial") return "bg-yellow-100 text-yellow-800";
    if (confidence === "no") return "bg-red-100 text-red-800";
    return "bg-green-100 text-green-800";
  }

  formatIncomeTaxAddress(record: any): string {
    const parts = [
      record.flatDoorBuilding || record.flat_door_building,
      record.nameOfPremisesBuildingVillage ||
        record.name_of_premises_building_village,
      record.areaLocality || record.area_locality,
      record.townCityDistrict || record.town_city_district,
      record.state,
      record.pincode || record.pin_code_zip_code,
    ].filter((part) => part && part.trim() !== "");
    return parts.join(", ") || "N/A";
  }

  get darpanTableData(): any[] {
    return this.darpanMatches.map((record: any) => ({
      ngoName: record.ngoName || "N/A",
      darpanId: record.darpanId || "N/A",
      ngoType: record.ngoType || "N/A",
      registrationNo: record.registrationNo || "N/A",
      registrationDate: this.formatDate(record.registrationDate),
      address: record.address || "N/A",
      website: record.ngoUrl || "N/A",
      matchLevel: this.formatMatchConfidence(record.matchLevel),
    }));
  }

  get blacklistTableData(): any[] {
    return this.blacklistMatches.map((record: any) => ({
      name: record.name || "N/A",
      nameCeoAndMd: record.nameCeoAndMd || "N/A",
      registrationNoAndDate: this.formatDate(record.registrationNoAndDate),
      debarringDate: this.formatDate(record.debarringDate),
      matchConfidence:
        (record.matchConfidence || "").split("_").join(" ").toUpperCase() ||
        "N/A",
      address: record.address || "N/A",
      reference: record.links ? `View (${record.links})` : "N/A",
    }));
  }

  get fcraTableData(): any[] {
    return this.fcraMatches.map((record: any) => ({
      associationName: record.associationName || "N/A",
      fcraRegistrationNumber: record.fcraRegistrationNumber || "N/A",
      associationAddress: record.associationAddress || "N/A",
      pincode: record.pincode || "N/A",
      sourceState: record.sourceState || "N/A",
      validityFromDate: this.commonService.formatDDMMYYYY(
        record.validityFromDate,
      ),
      validityToDate: this.commonService.formatDDMMYYYY(record.validityToDate),
      lastRenewedDate: this.commonService.formatDDMMYYYY(
        record.lastRenewedDate,
      ),
      matchLevel: this.formatMatchConfidence(record.matchConfidence),
    }));
  }

  get incomeTaxTableData(): any[] {
    return this.incomeTaxRawData.map((record: any) => {
      const sectionNumber = record.sectionNumber || record.section_number || "";
      const dateOfRegistration =
        record.dateOfRegistration || record.date_of_registration || "";
      const dateOfSurrender =
        record.dateOfSurrender || record.date_of_surrender || "";
      const ayDateFromRegistered =
        record.ayDateFromRegistered || record.ay_date_from_registered || "";
      return {
        registrationType: sectionNumber.includes("12A")
          ? "12A Registration"
          : sectionNumber.includes("80G")
            ? "80G Registration"
            : "N/A",
        section: sectionNumber || "N/A",
        registrationDate: this.formatDate(dateOfRegistration),
        urn:
          record.uniqueRegistrationNumber ||
          record.unique_registration_number ||
          "N/A",
        surrenderDate: this.formatDate(dateOfSurrender),
        assessmentYear: ayDateFromRegistered,
        address: this.formatIncomeTaxAddress(record),
      };
    });
  }

  getGSTFilingTableDataForGroup(group: {
    gstin: string;
    state: string;
    filings: any[];
  }): any[] {
    return group.filings.map((filing: any) => {
      const statusInfo = getGSTFilingStatus(
        filing.rtntype,
        filing.dof,
        filing.status,
      );
      return {
        rtntype: filing.rtntype || "N/A",
        ret_prd: filing.ret_prd || "N/A",
        dof: filing.dof || "N/A",
        arn: filing.arn || "N/A",
        status: filing.status || "N/A",
        timeliness: statusInfo.text,
      };
    });
  }

  downloadHTMLReport(): void {
    downloadDashboard(
      this.ngoData?.confirmedOrganizationName || "N/A",
      this.ngoData?.pan || "N/A",
    );
  }

  downloadPDFReport(): void {
    downloadDashboardAsPDF(
      this.ngoData?.confirmedOrganizationName || "N/A",
      this.ngoData?.pan || "N/A",
    );
  }

  @HostListener("window:beforeunload", ["$event"])
  blockReload(event: BeforeUnloadEvent) {
    if (this.txnId) return;
    event.preventDefault();
    event.returnValue = true;
  }
}
